import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Rocket, Plus, Trash2, History, Loader2, Receipt, LogOut } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { NavBar } from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyTier = () => ({ tier_id: crypto.randomUUID(), name: "", price: 4.99, billing: "one_time", features: [], badge: null });

const TierEditor = ({ tier, index, onChange, onRemove }) => (
  <div className="border border-border rounded-lg p-4 space-y-3 bg-card">
    <div className="flex items-center justify-between">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Tier {index + 1}</p>
      <Button variant="ghost" size="icon" data-testid={`remove-tier-btn-${index}`} onClick={onRemove} className="h-7 w-7 text-destructive hover:text-destructive">
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs">Name</Label>
        <Input data-testid={`tier-name-input-${index}`} value={tier.name} onChange={(e) => onChange({ ...tier, name: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">Price (USD)</Label>
        <Input data-testid={`tier-price-input-${index}`} type="number" step="0.01" min="0.5" value={tier.price}
          onChange={(e) => onChange({ ...tier, price: parseFloat(e.target.value) || 0 })} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs">Billing</Label>
        <select
          data-testid={`tier-billing-select-${index}`}
          value={tier.billing}
          onChange={(e) => onChange({ ...tier, billing: e.target.value })}
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="one_time">One-time</option>
          <option value="monthly">Monthly subscription</option>
        </select>
      </div>
      <div>
        <Label className="text-xs">Badge (optional)</Label>
        <Input data-testid={`tier-badge-input-${index}`} value={tier.badge || ""} placeholder="Most Popular"
          onChange={(e) => onChange({ ...tier, badge: e.target.value || null })} />
      </div>
    </div>
    <div>
      <Label className="text-xs">Features (one per line)</Label>
      <Textarea data-testid={`tier-features-input-${index}`} rows={3} value={tier.features.join("\n")}
        onChange={(e) => onChange({ ...tier, features: e.target.value.split("\n").filter(Boolean) })} />
    </div>
  </div>
);

export default function Admin({ dark, toggleTheme }) {
  const [form, setForm] = useState(null);
  const [releases, setReleases] = useState([]);
  const [txns, setTxns] = useState([]);
  const [releasing, setReleasing] = useState(false);
  const { user, logout } = useAuth();

  const load = async () => {
    const [s, r, t] = await Promise.all([
      axios.get(`${API}/showcase`),
      axios.get(`${API}/admin/releases`),
      axios.get(`${API}/admin/transactions`),
    ]);
    setForm({ ...s.data });
    setReleases(r.data);
    setTxns(t.data);
  };

  useEffect(() => { load().catch(() => toast.error("Failed to load config")); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const release = async () => {
    if (!form.app_name || !form.tagline) { toast.error("App name and tagline are required"); return; }
    if (form.tiers.some((t) => !t.name || t.price <= 0)) { toast.error("Every tier needs a name and a price > 0"); return; }
    setReleasing(true);
    try {
      const { app_name, tagline, description, icon_url, screenshot_url, features, tiers } = form;
      await axios.post(`${API}/admin/release`, { app_name, tagline, description, icon_url, screenshot_url, features, tiers });
      toast.success("Released! The showcase is now live.");
      await load();
    } catch {
      toast.error("Release failed");
    } finally {
      setReleasing(false);
    }
  };

  const activate = async (id) => {
    await axios.post(`${API}/admin/releases/${id}/activate`);
    toast.success("Release re-activated");
    await load();
  };

  if (!form) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <NavBar dark={dark} toggleTheme={toggleTheme} />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Developer Console</p>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium">Quick Release</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Swap the showcased app in seconds. Hit release and the public checkout page updates instantly — rinse and repeat for every app you promote.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-border" />}
            <span data-testid="admin-user-email" className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="outline" size="sm" data-testid="admin-logout-btn" className="rounded-full"
              onClick={async () => { await logout(); window.location.href = "/"; }}>
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-10">
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>App name</Label>
                <Input data-testid="admin-app-name-input" value={form.app_name} onChange={(e) => set("app_name", e.target.value)} />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input data-testid="admin-tagline-input" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="admin-description-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Icon URL</Label>
                <Input data-testid="admin-icon-url-input" value={form.icon_url} onChange={(e) => set("icon_url", e.target.value)} />
              </div>
              <div>
                <Label>Screenshot URL</Label>
                <Input data-testid="admin-screenshot-url-input" value={form.screenshot_url} onChange={(e) => set("screenshot_url", e.target.value)} />
              </div>
            </div>
            <div>
              <Label>App highlights (one per line)</Label>
              <Textarea data-testid="admin-features-input" rows={4} value={form.features.join("\n")}
                onChange={(e) => set("features", e.target.value.split("\n").filter(Boolean))} />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl tracking-tight font-medium">Upgrade tiers</h2>
              <Button variant="outline" size="sm" data-testid="add-tier-btn" className="rounded-full"
                onClick={() => set("tiers", [...form.tiers, emptyTier()])}>
                <Plus className="w-4 h-4 mr-1" /> Add tier
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {form.tiers.map((t, i) => (
                <TierEditor key={t.tier_id} tier={t} index={i}
                  onChange={(nt) => set("tiers", form.tiers.map((x, j) => (j === i ? nt : x)))}
                  onRemove={() => set("tiers", form.tiers.filter((_, j) => j !== i))} />
              ))}
            </div>

            <Button
              data-testid="quick-release-btn"
              onClick={release}
              disabled={releasing}
              size="lg"
              className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-medium px-8"
            >
              {releasing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
              Quick Release — Push Live
            </Button>
          </div>

          <div className="space-y-8">
            <div className="border border-border rounded-xl p-5 bg-card">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-heading font-medium tracking-tight">Release history</h3>
              </div>
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {releases.map((r) => (
                  <div key={r.id} data-testid={`release-row-${r.id}`} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{r.app_name}</p>
                      <p className="font-mono text-[10px] text-muted-foreground">{new Date(r.released_at).toLocaleString()}</p>
                    </div>
                    {r.active ? (
                      <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 shrink-0">Live</Badge>
                    ) : (
                      <Button variant="outline" size="sm" data-testid={`activate-release-btn-${r.id}`} className="rounded-full text-xs h-7 shrink-0" onClick={() => activate(r.id)}>
                        Re-activate
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-border rounded-xl p-5 bg-card">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-heading font-medium tracking-tight">Payments</h3>
              </div>
              <p className="text-[11px] text-muted-foreground mb-4">Privacy-first: only status is stored. No names, no emails.</p>
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="transactions-list">
                {txns.length === 0 && <p className="text-sm text-muted-foreground">No payments yet.</p>}
                {txns.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-sm border-b border-border/40 pb-2">
                    <div>
                      <p className="font-medium">{t.tier_name} <span className="text-muted-foreground">· {t.app_name}</span></p>
                      <p className="font-mono text-[10px] text-muted-foreground">${t.amount.toFixed(2)} · {new Date(t.created_at).toLocaleString()}</p>
                    </div>
                    <Badge variant={t.payment_status === "paid" ? "default" : "secondary"}
                      className={t.payment_status === "paid" ? "bg-emerald-600 text-white hover:bg-emerald-600" : ""}>
                      {t.payment_status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
